import React, { useState } from 'react';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { getDateLocale } from '@/utils/dateLocale';
import { MatchDetailCard } from '@/components/MatchDetailCard';
import { PredictionCard } from '@/components/PredictionCard';
import { CoefficientsCard } from '@/components/CoefficientsCard';
import { MainSection } from '@/components/layout/MainSection';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/api';
import { useMatchDetailContext } from '@/layouts/MatchDetailLayout/context';
import { isCompleted } from '@/constants/matchStatus';
import { SectionContent } from '@/components/layout/SectionContent';
import { TournamentMatchesLink } from '@/components/TournamentMatchesLink';
import predictionsStyles from '@/pages/MatchPredictionsPage.module.scss';

type ActiveForm = 'none' | 'prediction' | 'coefficients';

const DetailsTab: React.FC = () => {
  const {
    tournament,
    match,
    userPrediction,
    canEditPrediction,
    canEditCoefficients,
    isArchived,
    refetchPredictions,
    refetchMatch,
    isMobile,
    tournamentId,
    openedOutsideTournament,
  } = useMatchDetailContext();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['tournament']);

  // Hoisted form takeover state. Drives the MatchDetailCard's strip-swap on mobile/tablet
  // (mirrors `pages/account/SecurityTab`'s sibling-hide pattern). Desktop sidebar keeps its
  // Popup-based forms — unaffected by this state.
  const [activeForm, setActiveForm] = useState<ActiveForm>('none');

  const isFinished = isCompleted(match.status);
  const matchDate = new Date(match.match_date);

  const getOutcomeCoefficient = () => {
    if (!userPrediction) return undefined;
    const outcome = (userPrediction as any).predicted_outcome;
    if (outcome === 'team1_win') return match.team1_win_coefficient;
    if (outcome === 'team2_win') return match.team2_win_coefficient;
    return match.draw_coefficient;
  };

  const handleInlinePredictionSave = async (scoreTeam1: number, scoreTeam2: number, oddsScore?: number) => {
    if (!canEditPrediction) return;
    const getOutcome = (s1: number, s2: number) => {
      if (s1 > s2) return 'team1_win';
      if (s1 < s2) return 'team2_win';
      return 'draw';
    };
    const data: any = {
      match_id: match.id,
      tournament_id: parseInt(tournamentId),
      predicted_score_team1: scoreTeam1,
      predicted_score_team2: scoreTeam2,
      predicted_outcome: getOutcome(scoreTeam1, scoreTeam2),
    };
    if (tournament?.manualCoefficients && oddsScore !== undefined) {
      data.odds_score = oddsScore;
    }
    if ((userPrediction as any)?.id) {
      await apiClient.updatePrediction(String((userPrediction as any).id), data);
    } else {
      await apiClient.createPrediction(data);
    }
    // Invalidate the tournament-matches query so the prediction shows up
    // immediately when the user navigates back to the Matches tab. Mirrors
    // the centralised invalidation in PredictionForm — done here because
    // this inline-save path bypasses PredictionForm entirely.
    queryClient.invalidateQueries({
      predicate: query => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === 'tournament-matches' &&
          String(key[1]) === String(tournamentId)
        );
      },
    });
    await refetchPredictions();
    setActiveForm('none');
  };

  const handleInlineCoefficientSave = async (team1Coef: number, drawCoef: number, team2Coef: number) => {
    if (!canEditCoefficients) return;
    await apiClient.updateMatchCoefficients(tournamentId, String(match.id), {
      team1_win_coefficient: team1Coef,
      draw_coefficient: drawCoef,
      team2_win_coefficient: team2Coef,
    });
    await refetchMatch();
    setActiveForm('none');
  };

  const matchSubtitle = format(matchDate, 'd MMMM yyyy, HH:mm', { locale: getDateLocale() });

  // On desktop the sidebar's TournamentInfoCard already shows league chrome — hide the title/logo
  // here (subtitle stays as a tab anchor).
  return (
    <MainSection
      hideTitleOnDesktop
      title={tournament?.name || 'Tournament'}
      subtitle={matchSubtitle}
      logoUrl={tournament?.league?.logo_url || tournament?.leagueLogo}
      logoAlt={tournament?.league?.name || tournament?.leagueName}
      headerRight={openedOutsideTournament ? <TournamentMatchesLink tournamentId={tournamentId} /> : undefined}
    >
      <SectionContent>
        {isArchived && (
          <div className={predictionsStyles.lockedNotice} role="status">
            <p className={predictionsStyles.lockedNoticeTitle}>
              {t('tournament:tournamentArchivedNotice')}
            </p>
          </div>
        )}
        {isMobile ? (
          <MatchDetailCard
            match={{
              ...match,
              team1_logo_url: match.team1_logo_url,
              team2_logo_url: match.team2_logo_url,
            }}
            userPrediction={userPrediction}
            coefficients={{
              team1Win: match.team1_win_coefficient,
              draw: match.draw_coefficient,
              team2Win: match.team2_win_coefficient,
              source: match.coefficients_source,
              team1Bookmaker: match.team1_bookmaker,
              drawBookmaker: match.draw_bookmaker,
              team2Bookmaker: match.team2_bookmaker,
            }}
            canEditPrediction={canEditPrediction}
            onEditPrediction={() => setActiveForm('prediction')}
            canEditCoefficients={canEditCoefficients}
            onEditCoefficients={() => setActiveForm('coefficients')}
            activeForm={activeForm}
            predictionFormSlot={
              activeForm === 'prediction' ? (
                <PredictionCard
                  noChrome
                  initialEditMode
                  predictedScoreTeam1={userPrediction?.predicted_score_team1}
                  predictedScoreTeam2={userPrediction?.predicted_score_team2}
                  predictedOutcome={(userPrediction as any)?.predicted_outcome}
                  pointsEarned={isFinished ? userPrediction?.points_earned : undefined}
                  outcomeCoefficient={getOutcomeCoefficient()}
                  scoreCoefficient={(userPrediction as any)?.odds_score}
                  hideOddsInput={!tournament?.manualCoefficients}
                  matchId={match.id}
                  tournamentId={tournamentId}
                  team1Name={match.team1}
                  team2Name={match.team2}
                  team1LogoUrl={match.team1_logo_url}
                  team2LogoUrl={match.team2_logo_url}
                  canEdit={canEditPrediction}
                  onInlineSave={handleInlinePredictionSave}
                  onClose={() => setActiveForm('none')}
                />
              ) : null
            }
            coefficientsFormSlot={
              activeForm === 'coefficients' ? (
                <CoefficientsCard
                  noChrome
                  initialEditMode
                  team1Name={match.team1}
                  team2Name={match.team2}
                  team1Coefficient={match.team1_win_coefficient}
                  drawCoefficient={match.draw_coefficient}
                  team2Coefficient={match.team2_win_coefficient}
                  source={match.coefficients_source}
                  team1Bookmaker={match.team1_bookmaker}
                  drawBookmaker={match.draw_bookmaker}
                  team2Bookmaker={match.team2_bookmaker}
                  showEditButton={canEditCoefficients}
                  onInlineSave={handleInlineCoefficientSave}
                  onCancel={() => setActiveForm('none')}
                />
              ) : null
            }
          />
        ) : (
          // Desktop (≥lg): only hero + meta strips here — the sidebar owns PredictionCard +
          // CoefficientsCard via MatchDetailLayout, with their existing Popup edit forms.
          <MatchDetailCard
            match={{
              ...match,
              team1_logo_url: match.team1_logo_url,
              team2_logo_url: match.team2_logo_url,
            }}
          />
        )}
      </SectionContent>
    </MainSection>
  );
};

export default DetailsTab;
